import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { classifyPlayerSection } from '../../../utils/playerClassification';

/**
 * Mobile-native roster summary bar.
 *
 * Bible §14 mobile/desktop separation: this is NOT a port of desktop MyRoster's
 * inline summary JSX. It is a native mobile component that reads the SAME
 * canonical build_roster_response fields desktop uses —
 *   league_context.financial.{contract_salary, contract_type},
 *   league_context.roster.status, rosterData.available_cash,
 *   league.salary_settings —
 * and reuses the shared slot-based classifier (utils/playerClassification).
 * The derivation mirrors desktop's calculateSalarySubtotals /
 * calculateContractBuckets / calculateCapInfo exactly (same fields, same rules:
 * Bat/Pit by SLOT §4.5.9.1; FAAB = backend ledger, never a client formula per
 * §4.5.6.1 / Rule 9; Expiring = contract_type 'OP'; Decision = contract_type '1').
 *
 * Layout is mobile-native: a compact 3-col × 2-row grid that fits 375px with no
 * horizontal overflow (desktop uses a 5-across strip that would crush on a phone).
 */
const C = dynastyTheme.tokens.colors;
const round = (n) => Math.round(n || 0);

const computeSummary = (players, availableCash, league) => {
  const sub = { hitting: 0, pitching: 0, overall: 0 };
  const counts = { active: 0, bench: 0, dl: 0, minors: 0 };
  let opCount = 0, opOffBooks = 0, decisionCount = 0;

  (players || []).forEach((p) => {
    const status = p?.league_context?.roster?.status || 'bench';
    const salary = p?.league_context?.financial?.contract_salary || 0;
    const ctype = p?.league_context?.financial?.contract_type;
    const section = classifyPlayerSection(p);
    if (['active', 'bench', 'dl', 'minors'].includes(status)) {
      counts[status] += 1;
      if (section === 'pitching') sub.pitching += salary; else sub.hitting += salary;
      sub.overall += salary;
    }
    if (ctype === 'OP') { opCount += 1; opOffBooks += salary; }
    else if (ctype === '1') { decisionCount += 1; }
  });

  const ss = league?.salary_settings || {};
  const useDualCap = ss.use_dual_cap ?? true;
  const faabTotal = useDualCap
    ? ((ss.draft_cap || 600) + (ss.season_cap || 200))
    : (ss.salary_cap || 800);
  // §4.5.6.1 / Rule 9: FAAB is the backend ledger balance ONLY — no client-side
  // formula fallback. Missing → explicit unavailable state, never a fabricated $.
  const faab = availableCash != null ? availableCash : null;

  return { sub, counts, opCount, opOffBooks, decisionCount, faab, faabTotal, useDualCap };
};

const Cell = ({ label, labelColor, topBar, children }) => (
  <div style={{
    padding: '8px 10px',
    borderLeft: `1px solid ${C.hairline}`,
    borderTop: topBar ? `2px solid ${topBar}` : undefined,
    minWidth: 0,
  }}>
    <div style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: labelColor || C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    {children}
  </div>
);

const Big = ({ children, color }) => (
  <div style={{ fontSize: 17, fontWeight: 900, lineHeight: 1.2, color: color || C.textHi }}>{children}</div>
);
const Sub = ({ children }) => (
  <div style={{ fontSize: 8.5, color: C.muted, lineHeight: 1.35 }}>{children}</div>
);

const MobileRosterSummaryBar = ({ players, availableCash, league }) => {
  const s = computeSummary(players, availableCash, league);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        borderBottom: `1px solid ${C.hairline}`,
        background: C.surface?.[800] || 'transparent',
      }}
    >
      {/* Total Payroll */}
      <Cell label="Total Payroll">
        <Big>${round(s.sub.overall)}</Big>
        <Sub>Bat <span style={{ color: C.success }}>${round(s.sub.hitting)}</span> · Pit <span style={{ color: C.success }}>${round(s.sub.pitching)}</span></Sub>
      </Cell>

      {/* FAAB / Cap Space */}
      <Cell label={s.useDualCap ? 'FAAB' : 'Cap Space'}>
        {s.faab != null ? (
          <>
            <Big color={C.success}>${round(s.faab)}</Big>
            <Sub>of ${round(s.faabTotal)}</Sub>
          </>
        ) : (
          <div style={{ fontSize: 10, fontWeight: 700, marginTop: 2, color: C.danger }}>Balance unavailable</div>
        )}
      </Cell>

      {/* Roster counts */}
      <Cell label="Roster">
        <div style={{ fontSize: 11, marginTop: 2, lineHeight: 1.45, color: C.grayCool }}>
          {s.counts.active} active · {s.counts.bench} bench
          {(s.counts.dl > 0 || s.counts.minors > 0) && <br />}
          {s.counts.dl > 0 && `${s.counts.dl} DL`}
          {s.counts.dl > 0 && s.counts.minors > 0 && ' · '}
          {s.counts.minors > 0 && `${s.counts.minors} minors`}
        </div>
      </Cell>

      {/* Expiring · OP */}
      <Cell label="Expiring · OP" labelColor={C.error} topBar={C.error}>
        <Big>{s.opCount}</Big>
        <Sub>lost · <span style={{ color: C.success }}>${round(s.opOffBooks)}</span> off books</Sub>
      </Cell>

      {/* Decision Needed */}
      <Cell label="Decision Needed" labelColor={C.warning} topBar={C.warning}>
        <Big>{s.decisionCount}</Big>
        <Sub>extend or release</Sub>
      </Cell>

      {/* trailing empty cell keeps the 3-col grid flush */}
      <div style={{ borderLeft: `1px solid ${C.hairline}` }} />
    </div>
  );
};

export default MobileRosterSummaryBar;
